import { useEffect, useRef } from "react";
import { pushDevDebugEvent } from "../utils/devDebugBuffer";
import { DEV_LOG_TITLE } from "../utils/devLogTitle";

/**
 * Dev-only guard that warns if a component renders "too many" times in a short window.
 * Helps catch accidental render loops (e.g., unstable props causing effect churn).
 *
 * This hook MUST NOT set state (to avoid making loops worse).
 */
export default function useDevRenderLoopGuard(
  name,
  { maxRenders = 80, windowMs = 2000 } = {},
) {
  const countRef = useRef(0);
  const startRef = useRef(typeof performance !== "undefined" ? performance.now() : Date.now());

  // Count renders (ref update does not trigger re-renders)
  countRef.current += 1;

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const elapsed = now - startRef.current;

    // Reset window
    if (elapsed > windowMs) {
      startRef.current = now;
      countRef.current = 0;
      return;
    }

    if (countRef.current > maxRenders) {
      const msg = `${DEV_LOG_TITLE} RenderLoopGuard ${name} rendered ${countRef.current} times in ${Math.round(elapsed)}ms`;
      pushDevDebugEvent({
        type: "render-loop-guard",
        name,
        renders: countRef.current,
        elapsedMs: Math.round(elapsed),
        stack: new Error().stack,
      });
      // Log once per window to avoid spamming
      // eslint-disable-next-line no-console
      console.warn(msg);
      startRef.current = now;
      countRef.current = 0;
    }
  });
}

